import { TouchBackend } from 'react-dnd-touch-backend';
import { DndProvider } from 'react-dnd';
import { ChakraEmptyList } from 'app/components/Core';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useContextMenu } from 'app/components/ChakraTableList';
import { NoteDialog } from 'app/components/Playlist/NoteDialog';
import {
  AudioDialogState,
  PlaylistRoomAudiosListProps,
} from 'app/components/Playlist/PlaylistRoomAudiosList/types';
import { audioDialogDefaultState } from 'app/components/Playlist/PlaylistRoomAudiosList/constants';
import { DeleteAudioDialog } from 'app/components/Playlist/DeleteAudioDialog';
import { ReactComponent as PlaylistsIcon } from 'resources/images/playlists-huge.svg';
import { ListDragLayer } from 'app/components/DraggableListRow';
import { useAudiosListReorder } from 'app/components/Playlist/PlaylistRoomAudiosList/hooks';
import { StickyTable } from 'app/components/Core/StickyTable/StickyTable';
import { Row } from '@tanstack/react-table';
import { contextMenuHandler } from 'app/components/Table/ContextMenuButton/utils';
import { PlaylistAudio } from 'types/playlists.types';
import { useTotalWidth } from 'app/components/Core/StickyTable';
import { getColumns } from './utils';
import { RenameDialog } from '../RenameDialog';
import { PlaylistAudioRow } from './PlaylistAudioRow';

export const PlaylistRoomAudiosList = ({
  audios,
  isLoading,
  onRemoveAudio,
  loadPlaylist,
}: PlaylistRoomAudiosListProps) => {
  const [audioRenameDialog, setAudioRenameDialog] = useState<AudioDialogState>(
    audioDialogDefaultState
  );
  const [audioRemoveDialog, setAudioRemoveDialog] = useState<AudioDialogState>(
    audioDialogDefaultState
  );
  const [noteDialog, setNoteDialog] = useState<AudioDialogState>(
    audioDialogDefaultState
  );
  const { tooltipTitle, rows, onDragging, onRowReorder } = useAudiosListReorder(
    audios
  );

  const ref = useRef<HTMLDivElement>(null);
  const tableKey = 'playlist';

  const totalWidth = useTotalWidth(ref);
  const columns = useMemo(() => getColumns(totalWidth), [totalWidth]);
  const contextMenuProps = useContextMenu();
  const rowRenderer = useCallback(
    (item: Row<PlaylistAudio>, index) => {
      const audio = item.original;
      const options = [
        {
          title: 'Rename',
          value: 'RENAME_PLAYLIST_AUDIO',
          onClick: () => setAudioRenameDialog({ audio, open: true }),
        },
        {
          title: 'Remove',
          value: 'REMOVE',
          onClick: () => setAudioRemoveDialog({ audio, open: true }),
        },
        {
          title: 'Edit note',
          value: 'EDIT_NOTE',
          onClick: () => setNoteDialog({ open: true, audio }),
        },
      ];

      return (
        <PlaylistAudioRow
          key={audio.guid}
          index={index}
          audio={audio}
          loadPlaylist={loadPlaylist}
          setNoteDialog={setNoteDialog}
          onDragging={onDragging}
          onRowReorder={onRowReorder}
          contextHandler={(e) =>
            contextMenuHandler(e, contextMenuProps, options)
          }
        />
      );
    },
    [contextMenuProps, loadPlaylist, onDragging, onRowReorder]
  );

  return (
    <>
      {!isLoading && !audios.length ? (
        <ChakraEmptyList
          icon={<PlaylistsIcon role="img" />}
          title="No Audio"
          primaryText="Populate this playlist by adding audio from your Songs."
        />
      ) : (
        <DndProvider
          backend={TouchBackend}
          options={{ enableMouseEvents: true }}
        >
          <StickyTable<PlaylistAudio>
            tableKey={tableKey}
            ref={ref}
            data={rows}
            columns={columns}
            rowRenderer={rowRenderer}
            loading={isLoading}
            contextMenuProps={contextMenuProps}
          />
          <ListDragLayer title={tooltipTitle || ''} />
        </DndProvider>
      )}
      <NoteDialog
        isOpen={noteDialog.open}
        playlistAudio={noteDialog?.audio}
        onClose={() => setNoteDialog({ ...noteDialog, open: false })}
        onCloseComplete={() => setNoteDialog({ audio: null, open: false })}
      />
      <RenameDialog
        isOpen={audioRenameDialog.open}
        playlistAudio={audioRenameDialog?.audio}
        onClose={() =>
          setAudioRenameDialog({ ...audioRenameDialog, open: false })
        }
        onCloseComplete={() =>
          setAudioRenameDialog({ audio: null, open: false })
        }
      />
      <DeleteAudioDialog
        audioRemoveDialog={audioRemoveDialog}
        setAudioRemoveDialog={setAudioRemoveDialog}
        onRemoveAudio={onRemoveAudio}
      />
    </>
  );
};
